<template>
  <div>
    <vx-card title="Region-Plan Management" subtitle="Configure which plans are available in each region">
      <!-- Filters -->
      <div class="flex items-end mb-4">
        <vs-select
          v-model="selectedRegion"
          placeholder="Filter by Region"
          class="w-full md:w-1/3 mr-4"
          @input="loadMatrix"
        >
          <vs-select-item value="all" text="All Regions" />
          <vs-select-item
            v-for="region in regions"
            :key="region.id"
            :value="region.id"
            :text="`${region.name} (${region.country_code})`"
          />
        </vs-select>

        <vs-button @click="loadMatrix" color="primary">
          <i class="bx bx-refresh"></i>
          Refresh
        </vs-button>
      </div>

      <!-- Loading -->
      <div v-if="loading" class="flex justify-center items-center py-12">
        <vs-progress indeterminate color="primary">Loading...</vs-progress>
      </div>

      <!-- Matrix Table -->
      <div v-else class="overflow-x-auto">
        <table class="w-full">
          <thead>
            <tr>
              <th class="px-4 py-3 text-left bg-gray-100 dark:bg-gray-800 border-b">
                Region / Plan
              </th>
              <th
                v-for="plan in plans"
                :key="plan.id"
                class="px-4 py-3 text-center bg-gray-100 dark:bg-gray-800 border-b min-w-32"
              >
                <div class="font-semibold text-sm">{{ plan.name }}</div>
                <div class="text-xs text-gray-500 dark:text-gray-400">
                  {{ formatPrice(plan.price_monthly) }} / mo
                </div>
                <div class="text-xs text-gray-400">
                  {{ plan.vcpus }}vCPU, {{ plan.memory_base_mb }}MB, {{ plan.storage_gb }}GB
                </div>
                <div class="text-xs mt-1 font-semibold text-blue-600">
                  {{ plan.slug }}
                </div>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="row in filteredMatrix"
              :key="row.region.id"
              class="border-b hover:bg-gray-50 dark:hover:bg-gray-900"
            >
              <td class="px-4 py-3 font-semibold">
                <div class="flex items-center">
                  <span class="flag-icon mr-2">{{ getFlagEmoji(row.region.country_code) }}</span>
                  <div>
                    <div>{{ row.region.name }}</div>
                    <div class="text-xs text-gray-500 dark:text-gray-400">
                      {{ row.region.city }}
                    </div>
                  </div>
                </div>
              </td>
              <td
                v-for="planData in row.plans"
                :key="planData.plan.id"
                class="px-4 py-3 text-center"
                :class="{ 'bg-green-50 dark:bg-green-900': planData.is_available, 'bg-gray-100 dark:bg-gray-800 opacity-50': !planData.is_available }"
              >
                <div class="flex flex-col items-center">
                  <!-- Status Badge -->
                  <div class="mb-2">
                    <span v-if="planData.is_available" class="px-2 py-1 text-xs font-semibold rounded bg-green-500 text-white">
                      ✓ Visible in Console
                    </span>
                    <span v-else class="px-2 py-1 text-xs font-semibold rounded bg-red-500 text-white">
                      ✗ Hidden from Users
                    </span>
                  </div>

                  <!-- Availability Toggle -->
                  <vs-switch
                    :value="planData.is_available"
                    @input="toggleAvailability(row.region.id, planData.plan.id, $event)"
                  >
                    <template #off>Disabled</template>
                    <template #on>Enabled</template>
                  </vs-switch>

                  <!-- Price Override -->
                  <div v-if="planData.is_available" class="mt-2 w-full">
                    <vs-input
                      :value="planData.price_override"
                      :placeholder="`Default: ${formatPrice(planData.plan.price_monthly)}`"
                      type="number"
                      min="0"
                      size="small"
                      class="w-full"
                      @blur="updatePriceOverride(row.region.id, planData.plan.id, $event.target.value)"
                    >
                      <template #icon>
                        <i class="bx bx-dollar-circle"></i>
                      </template>
                    </vs-input>
                    <div class="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      <span v-if="planData.price_override">
                        Custom: {{ formatPrice(planData.price_override) }}
                      </span>
                      <span v-else>
                        Using default price
                      </span>
                    </div>
                  </div>

                  <!-- Traffic Override -->
                  <div v-if="planData.is_available" class="mt-2 w-full">
                    <vs-input
                      :value="planData.traffic_override"
                      :placeholder="`Default: ${planData.plan.bandwidth_quota_gb}GB`"
                      type="number"
                      min="0"
                      size="small"
                      class="w-full"
                      @blur="updateTrafficOverride(row.region.id, planData.plan.id, $event.target.value)"
                    >
                      <template #icon>
                        <i class="bx bx-transfer"></i>
                      </template>
                    </vs-input>
                    <div class="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      <span v-if="planData.traffic_override">
                        Custom: {{ planData.traffic_override }}GB
                      </span>
                      <span v-else>
                        Using default traffic
                      </span>
                    </div>
                  </div>

                  <!-- Bandwidth Ingress Override (Download to VM) -->
                  <div v-if="planData.is_available" class="mt-2 w-full">
                    <label class="text-xs text-gray-600 dark:text-gray-300">Ingress (Download)</label>
                    <vs-input
                      :value="planData.bandwidth_ingress_override"
                      :placeholder="`Default: ${planData.plan.bandwidth_ingress_mbps}Mbps`"
                      type="number"
                      min="0"
                      size="small"
                      class="w-full"
                      @blur="updateBandwidthIngressOverride(row.region.id, planData.plan.id, $event.target.value)"
                    >
                      <template #icon>
                        <i class="bx bx-download"></i>
                      </template>
                    </vs-input>
                    <div class="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      <span v-if="planData.bandwidth_ingress_override">
                        Custom: {{ planData.bandwidth_ingress_override }}Mbps
                      </span>
                      <span v-else>
                        Using default ingress
                      </span>
                    </div>
                  </div>

                  <!-- Bandwidth Egress Override (Upload from VM) -->
                  <div v-if="planData.is_available" class="mt-2 w-full">
                    <label class="text-xs text-gray-600 dark:text-gray-300">Egress (Upload)</label>
                    <vs-input
                      :value="planData.bandwidth_egress_override"
                      :placeholder="`Default: ${planData.plan.bandwidth_egress_mbps}Mbps`"
                      type="number"
                      min="0"
                      size="small"
                      class="w-full"
                      @blur="updateBandwidthEgressOverride(row.region.id, planData.plan.id, $event.target.value)"
                    >
                      <template #icon>
                        <i class="bx bx-upload"></i>
                      </template>
                    </vs-input>
                    <div class="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      <span v-if="planData.bandwidth_egress_override">
                        Custom: {{ planData.bandwidth_egress_override }}Mbps
                      </span>
                      <span v-else>
                        Using default egress
                      </span>
                    </div>
                  </div>

                  <!-- Stock Limit -->
                  <div v-if="planData.is_available" class="mt-2 w-full">
                    <vs-input
                      :value="planData.stock_limit"
                      placeholder="Unlimited"
                      type="number"
                      min="0"
                      size="small"
                      class="w-full"
                      @blur="updateStockLimit(row.region.id, planData.plan.id, $event.target.value)"
                    >
                      <template #icon>
                        <i class="bx bx-box"></i>
                      </template>
                    </vs-input>
                    <div class="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      In use: {{ planData.current_stock }}
                      <span v-if="planData.stock_limit">
                        / {{ planData.stock_limit }}
                      </span>
                    </div>
                    <div
                      v-if="!planData.in_stock"
                      class="text-xs text-danger mt-1"
                    >
                      Out of Stock
                    </div>
                  </div>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Empty State -->
      <div v-if="!loading && matrix.length === 0" class="text-center py-12">
        <i class="bx bx-grid-alt text-6xl text-gray-400 mb-4"></i>
        <p class="text-gray-500 dark:text-gray-400">No region-plan data available</p>
      </div>
    </vx-card>
  </div>
</template>

<script>
export default {
  name: 'HaioFlashRegionPlans',
  data() {
    return {
      loading: false,
      regions: [],
      plans: [],
      matrix: [],
      selectedRegion: 'all',
    }
  },
  computed: {
    filteredMatrix() {
      if (this.selectedRegion === 'all') {
        return this.matrix
      }
      return this.matrix.filter(row => row.region.id === this.selectedRegion)
    }
  },
  mounted() {
    this.loadMatrix()
  },
  methods: {
    async loadMatrix() {
      this.loading = true
      try {
        const response = await this.$http.get('/admin/haioflash/region-plans/matrix')
        if (response.data.success) {
          this.regions = response.data.regions
          this.plans = response.data.plans
          this.matrix = response.data.matrix
        } else {
          this.$vs.notify({
            title: 'Error',
            text: response.data.message || 'Failed to load region-plan matrix',
            color: 'danger',
          })
        }
      } catch (error) {
        console.error('Error loading matrix:', error)
        this.$vs.notify({
          title: 'Error',
          text: (error.response && error.response.data && error.response.data.message) || 'Failed to load region-plan matrix',
          color: 'danger',
        })
      } finally {
        this.loading = false
      }
    },

    async toggleAvailability(regionId, planId, isAvailable) {
      try {
        // Get existing values
        const row = this.matrix.find(r => r.region.id === regionId)
        const planData = row ? row.plans.find(p => p.plan.id === planId) : null
        
        const response = await this.$http.post('/admin/haioflash/region-plans/availability', {
          region_id: regionId,
          plan_id: planId,
          is_available: isAvailable,
          price_override: planData ? planData.price_override : null,
          traffic_override: planData ? planData.traffic_override : null,
          bandwidth_ingress_override: planData ? planData.bandwidth_ingress_override : null,
          bandwidth_egress_override: planData ? planData.bandwidth_egress_override : null,
          stock_limit: planData ? planData.stock_limit : null,
        })

        if (response.data.success) {
          this.$vs.notify({
            title: 'Success',
            text: 'Plan availability updated',
            color: 'success',
          })
          // Update local data
          if (planData) {
            planData.is_available = isAvailable
          }
        }
      } catch (error) {
        console.error('Error updating availability:', error)
        this.$vs.notify({
          title: 'Error',
          text: (error.response && error.response.data && error.response.data.message) || 'Failed to update availability',
          color: 'danger',
        })
        // Reload to revert changes
        this.loadMatrix()
      }
    },

    async updatePriceOverride(regionId, planId, priceOverride) {
      try {
        const price = priceOverride === '' || priceOverride === null ? null : parseInt(priceOverride)
        
        // Get existing values
        const row = this.matrix.find(r => r.region.id === regionId)
        const planData = row ? row.plans.find(p => p.plan.id === planId) : null
        
        const response = await this.$http.post('/admin/haioflash/region-plans/availability', {
          region_id: regionId,
          plan_id: planId,
          is_available: planData ? planData.is_available : true,
          price_override: price,
          traffic_override: planData ? planData.traffic_override : null,
          bandwidth_ingress_override: planData ? planData.bandwidth_ingress_override : null,
          bandwidth_egress_override: planData ? planData.bandwidth_egress_override : null,
          stock_limit: planData ? planData.stock_limit : null,
        })

        if (response.data.success) {
          this.$vs.notify({
            title: 'Success',
            text: 'Price override updated',
            color: 'success',
          })
          // Update local data
          if (planData) {
            planData.price_override = price
          }
        }
      } catch (error) {
        console.error('Error updating price override:', error)
        this.$vs.notify({
          title: 'Error',
          text: (error.response && error.response.data && error.response.data.message) || 'Failed to update price override',
          color: 'danger',
        })
      }
    },

    async updateStockLimit(regionId, planId, stockLimit) {
      try {
        const limit = stockLimit === '' || stockLimit === null ? null : parseInt(stockLimit)
        
        // Get existing values
        const row = this.matrix.find(r => r.region.id === regionId)
        const planData = row ? row.plans.find(p => p.plan.id === planId) : null
        
        const response = await this.$http.post('/admin/haioflash/region-plans/availability', {
          region_id: regionId,
          plan_id: planId,
          is_available: planData ? planData.is_available : true,
          price_override: planData ? planData.price_override : null,
          traffic_override: planData ? planData.traffic_override : null,
          bandwidth_ingress_override: planData ? planData.bandwidth_ingress_override : null,
          bandwidth_egress_override: planData ? planData.bandwidth_egress_override : null,
          stock_limit: limit,
        })

        if (response.data.success) {
          this.$vs.notify({
            title: 'Success',
            text: 'Stock limit updated',
            color: 'success',
          })
          // Update local data
          if (planData) {
            planData.stock_limit = limit
            planData.in_stock = limit === null || planData.current_stock < limit
          }
        }
      } catch (error) {
        console.error('Error updating stock limit:', error)
        this.$vs.notify({
          title: 'Error',
          text: (error.response && error.response.data && error.response.data.message) || 'Failed to update stock limit',
          color: 'danger',
        })
      }
    },

    async updateTrafficOverride(regionId, planId, trafficOverride) {
      try {
        const traffic = trafficOverride === '' || trafficOverride === null ? null : parseInt(trafficOverride)
        
        // Get existing values
        const row = this.matrix.find(r => r.region.id === regionId)
        const planData = row ? row.plans.find(p => p.plan.id === planId) : null
        
        const response = await this.$http.post('/admin/haioflash/region-plans/availability', {
          region_id: regionId,
          plan_id: planId,
          is_available: planData ? planData.is_available : true,
          price_override: planData ? planData.price_override : null,
          traffic_override: traffic,
          bandwidth_ingress_override: planData ? planData.bandwidth_ingress_override : null,
          bandwidth_egress_override: planData ? planData.bandwidth_egress_override : null,
          stock_limit: planData ? planData.stock_limit : null,
        })

        if (response.data.success) {
          this.$vs.notify({
            title: 'Success',
            text: 'Traffic override updated',
            color: 'success',
          })
          // Update local data
          if (planData) {
            planData.traffic_override = traffic
          }
        }
      } catch (error) {
        console.error('Error updating traffic override:', error)
        this.$vs.notify({
          title: 'Error',
          text: (error.response && error.response.data && error.response.data.message) || 'Failed to update traffic override',
          color: 'danger',
        })
      }
    },

    async updateBandwidthIngressOverride(regionId, planId, bandwidthIngressOverride) {
      try {
        const bandwidth = bandwidthIngressOverride === '' || bandwidthIngressOverride === null ? null : parseInt(bandwidthIngressOverride)
        
        // Get existing values
        const row = this.matrix.find(r => r.region.id === regionId)
        const planData = row ? row.plans.find(p => p.plan.id === planId) : null
        
        const response = await this.$http.post('/admin/haioflash/region-plans/availability', {
          region_id: regionId,
          plan_id: planId,
          is_available: planData ? planData.is_available : true,
          price_override: planData ? planData.price_override : null,
          traffic_override: planData ? planData.traffic_override : null,
          bandwidth_ingress_override: bandwidth,
          bandwidth_egress_override: planData ? planData.bandwidth_egress_override : null,
          stock_limit: planData ? planData.stock_limit : null,
        })

        if (response.data.success) {
          this.$vs.notify({
            title: 'Success',
            text: 'Ingress bandwidth override updated',
            color: 'success',
          })
          // Update local data
          if (planData) {
            planData.bandwidth_ingress_override = bandwidth
          }
        }
      } catch (error) {
        console.error('Error updating ingress bandwidth override:', error)
        this.$vs.notify({
          title: 'Error',
          text: (error.response && error.response.data && error.response.data.message) || 'Failed to update ingress bandwidth override',
          color: 'danger',
        })
      }
    },

    async updateBandwidthEgressOverride(regionId, planId, bandwidthEgressOverride) {
      try {
        const bandwidth = bandwidthEgressOverride === '' || bandwidthEgressOverride === null ? null : parseInt(bandwidthEgressOverride)
        
        // Get existing values
        const row = this.matrix.find(r => r.region.id === regionId)
        const planData = row ? row.plans.find(p => p.plan.id === planId) : null
        
        const response = await this.$http.post('/admin/haioflash/region-plans/availability', {
          region_id: regionId,
          plan_id: planId,
          is_available: planData ? planData.is_available : true,
          price_override: planData ? planData.price_override : null,
          traffic_override: planData ? planData.traffic_override : null,
          bandwidth_ingress_override: planData ? planData.bandwidth_ingress_override : null,
          bandwidth_egress_override: bandwidth,
          stock_limit: planData ? planData.stock_limit : null,
        })

        if (response.data.success) {
          this.$vs.notify({
            title: 'Success',
            text: 'Egress bandwidth override updated',
            color: 'success',
          })
          // Update local data
          if (planData) {
            planData.bandwidth_egress_override = bandwidth
          }
        }
      } catch (error) {
        console.error('Error updating egress bandwidth override:', error)
        this.$vs.notify({
          title: 'Error',
          text: (error.response && error.response.data && error.response.data.message) || 'Failed to update egress bandwidth override',
          color: 'danger',
        })
      }
    },

    formatPrice(price) {
      return new Intl.NumberFormat('fa-IR').format(price) + ' ریال'
    },

    getFlagEmoji(countryCode) {
      const flagMap = {
        IR: '🇮🇷',
        FR: '🇫🇷',
        DE: '🇩🇪',
        CA: '🇨🇦',
        US: '🇺🇸',
        GB: '🇬🇧',
      }
      return flagMap[countryCode] || '🌍'
    }
  }
}
</script>

<style scoped>
table {
  border-collapse: collapse;
}

th, td {
  border: 1px solid rgba(0, 0, 0, 0.1);
}

.dark th,
.dark td {
  border-color: rgba(255, 255, 255, 0.1);
}
</style>
